import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../api/client';
import LoadingSpinner from '../../components/LoadingSpinner';
import DatePickerInput from '../../components/DatePickerInput';

export default function PeriodClose() {
  const location = useLocation();
  const aiPrefill = (location.state as { aiPrefill?: { fields: Record<string, unknown>; explanation?: string } } | null)?.aiPrefill;

  const [form, setForm] = useState({
    closeAtDate: new Date().toISOString().split('T')[0] ?? '',
    consolidatedAccount: 0,
    currentProfitAccount: 0,
    formerProfitAccount: 0,
    isAnnualClose: false,
  });
  const [showBanner, setShowBanner] = useState(false);

  // Apply AI prefill
  useEffect(() => {
    if (aiPrefill?.fields) {
      const f = aiPrefill.fields;
      setForm(prev => ({
        ...prev,
        closeAtDate: f.closeAtDate ? String(f.closeAtDate) : prev.closeAtDate,
        consolidatedAccount: f.consolidatedAccount ? Number(f.consolidatedAccount) : prev.consolidatedAccount,
        currentProfitAccount: f.currentProfitAccount ? Number(f.currentProfitAccount) : prev.currentProfitAccount,
        formerProfitAccount: f.formerProfitAccount ? Number(f.formerProfitAccount) : prev.formerProfitAccount,
        isAnnualClose: f.isAnnualClose ? Boolean(f.isAnnualClose) : prev.isAnnualClose,
      }));
      setShowBanner(true);
    }
  }, [aiPrefill]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleClose = async () => {
    if (!confirm(`Close period at ${form.closeAtDate}? This action cannot be undone.`)) return;

    setLoading(true);
    setResult(null);
    try {
      const entryId = await api.post<number>('/periods/close', {
        closeAtDate: form.closeAtDate,
        consolidatedAccount: form.consolidatedAccount,
        currentProfitAccount: form.currentProfitAccount,
        formerProfitAccount: form.formerProfitAccount,
        isAnnualClose: form.isAnnualClose,
      });
      setResult({ success: true, message: `Period closed successfully. Journal entry #${entryId} created.` });
    } catch (err) {
      setResult({ success: false, message: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAutoClose = async () => {
    if (!confirm(`Auto-close period at ${form.closeAtDate}? This will use company default accounts. This action cannot be undone.`)) return;

    setLoading(true);
    setResult(null);
    try {
      const entryId = await api.post<number>('/periods/auto-close', {
        closeAtDate: form.closeAtDate,
        isAnnualClose: form.isAnnualClose,
      });
      setResult({ success: true, message: `Period auto-closed successfully. Journal entry #${entryId} created.` });
    } catch (err) {
      setResult({ success: false, message: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <h1 className="text-xl font-bold text-gray-900">Period Closing</h1>

      {showBanner && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center">
          <div>
            <p className="text-sm font-medium text-blue-800">AI užpildė šią formą</p>
            {aiPrefill?.explanation && <p className="text-xs text-blue-600 mt-0.5">{aiPrefill.explanation}</p>}
          </div>
          <button onClick={() => setShowBanner(false)} className="text-blue-400 hover:text-blue-600 text-sm">✕</button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-sm text-gray-600 mb-4">
          Close an accounting period. This zeroes out income (class 5) and expense (class 6) accounts
          and transfers the result through the Consolidated Account to Current Profit (Steps 1-2).
          For annual closes only, Current Profit is further transferred to Former Profit (Step 3),
          as required after annual financial statements are approved per Lithuanian company law.
        </p>

        <div className="space-y-3">
          <DatePickerInput
            label="Close At Date *"
            value={form.closeAtDate}
            onChange={(v) => setForm({ ...form, closeAtDate: v })}
            required
          />
          <div>
            <label className="block text-xs text-gray-500 mb-1">Consolidated Account *</label>
            <input
              type="number"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              placeholder="e.g., 3500"
              value={form.consolidatedAccount || ''}
              onChange={(e) => setForm({ ...form, consolidatedAccount: parseInt(e.target.value) || 0 })}
            />
            <p className="text-xs text-gray-400 mt-1">Technical account for zeroing out income/expense</p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Current Profit Account</label>
            <input
              type="number"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              placeholder="e.g., 3501"
              value={form.currentProfitAccount || ''}
              onChange={(e) => setForm({ ...form, currentProfitAccount: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Former Profit Account</label>
            <input
              type="number"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              placeholder="e.g., 3502"
              value={form.formerProfitAccount || ''}
              onChange={(e) => setForm({ ...form, formerProfitAccount: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="isAnnualClose"
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              checked={form.isAnnualClose}
              onChange={(e) => setForm({ ...form, isAnnualClose: e.target.checked })}
            />
            <label htmlFor="isAnnualClose" className="text-sm text-gray-700">
              Annual close (transfer Current Profit → Former Profit)
            </label>
          </div>
          {form.isAnnualClose && (
            <p className="text-xs text-amber-600">
              Annual close will transfer the accumulated result from Current Profit to Former Profit account.
              This should only be done after the annual financial statements are approved.
            </p>
          )}
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            onClick={handleClose}
            disabled={loading || !form.closeAtDate || !form.consolidatedAccount}
          >
            {loading ? 'Closing...' : 'Close Period'}
          </button>
          <button
            className="px-4 py-2 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
            onClick={handleAutoClose}
            disabled={loading || !form.closeAtDate}
          >
            {loading ? 'Auto-Closing...' : 'Auto-Close (use defaults)'}
          </button>
          {loading && <LoadingSpinner />}
        </div>
      </div>

      {result && (
        <div className={`rounded-lg border p-4 ${result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <p className={`text-sm ${result.success ? 'text-green-800' : 'text-red-800'}`}>
            {result.message}
          </p>
        </div>
      )}
    </div>
  );
}
